import { useEffect } from "react";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { useFormik } from 'formik';
import * as yup from 'yup';
import useClients from "../../../hooks/api-calls/useClients";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { toast } from "sonner";
import { ActionIcon, Button, Modal, Switch, Text, Textarea } from "rizzui";
import { IoClose } from "react-icons/io5";
import { ParkService } from "../../../services/park/parkService";
import RequiredInput from "../../../components/forms/InputHOC";
import CustomSelect from "../../../components/forms/CustomSelect";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import FormLabel from "../../../components/forms/FormLabel";


interface ParkFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    park: any;
}


const schema = yup.object({
    name: yup.string().required('Campo obligatorio'),
    alias: yup.string().required('Campo obligatorio'),
    location: yup.string().required('Campo obligatorio'),
    clientId: yup.string().required('Campo obligatorio'),
    status: yup.boolean()
});

const ParkFormModal: React.FC<ParkFormModalProps> = ({isOpen, onClose, refetch, park }) => {
    
    // STATES

    const service = new ParkService();
    const isEdit = park !== null;
   
    
    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { getClientsList } = useClients();
    const { getCompaniesList } = useCompanies();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: '',
            name: '',
            alias: '',
            municipal: '',
            location: '', 
            clientId: '',
            status: true,
            company: '',
            capacity: '',
        },
        validationSchema: schema,
        validateOnBlur: false,
        //validateOnChange: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO PARQUE
     * @EN CREATE A NEW PARK
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createPark(values)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Parque creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN PARQUE
     * @EN EDIT A PARK
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editPark(values)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Parque editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    // USE EFFECT

    useEffect(() => {
        if (isEdit) {
            formik.setFieldValue('id', park.id);
            formik.setFieldValue('name', park.name);
            formik.setFieldValue('alias', park.alias);
            formik.setFieldValue('municipal', park.municipal);
            formik.setFieldValue('location', park.location);
            formik.setFieldValue('clientId', park.client?.id);
            formik.setFieldValue('status', park.status);
            formik.setFieldValue('company', park.company.id);
            formik.setFieldValue('capacity', park.capacity);
        } else {
            formik.resetForm();
        }
    }, [park]);

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size='sm'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-xl !shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-4'>
                <div className='mb-7 flex items-center justify-between'>
                    <Text className="text-xl">{isEdit ? 'Editar ' : 'Crear '} parque eólico</Text>
                    <ActionIcon size='sm' variant='text' onClick={onClose}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                <RequiredInput
                    id='name'
                    label="Nombre"
                    formik={formik}
                    required
                    className="mb-4"
                />

                <RequiredInput
                    id='alias'
                    label="Alias"
                    formik={formik}
                    required
                    className="mb-4"
                />

                <RequiredInput
                    id='municipal'
                    label="Término municipal"
                    formik={formik}
                    required
                    className="mb-4"
                />

                <FormLabel label="Ubicación" required>
                    <Textarea
                        id='location'
                        required
                        rows={6}
                        value={formik.values.location}
                        onChange={(e) => formik.setFieldValue('location', e.target.value)}
                        className="mb-4"
                    />
                </FormLabel>
                {formik.errors.location && formik.touched.location && (
                    <Text className="text-red-500 text-sm mt-1">
                        {formik.errors.location}
                    </Text>
                )}
                <RequiredInput
                    id='capacity'
                    label="Capacidad"
                    formik={formik}
                    className="mb-4"
                    type={'number'}
                />
                
                {userCan('list', 'companies') ? (
                    <CustomSelect
                        isSearchable
                        id={'company'}
                        label="Compañia"
                        options={getCompaniesList()}
                        onChange={(e: any) => { 
                            formik.setFieldValue('company', e.value)
                        }}
                        value={
                            getCompaniesList().filter((company: any) => company.value === formik.values.company)
                        }
                        className="mb-4"
                    />
                ) : null}

                <CustomSelect
                    isSearchable
                    id={'clientId'}
                    label="Cliente"
                    options={getClientsList()}
                    onChange={(e: any) => {
                        formik.setFieldValue('clientId', e.value)
                    }}
                    value={
                        getClientsList().filter((client: any) => client.value === formik.values.clientId)
                    }
                    required
                    className="mb-4"
                />

                <Switch
                    id='status'
                    checked={formik.values.status} // Vinculado al valor actual en Formik
                    onChange={() => formik.setFieldValue('status', !formik.values.status)} // Cambia true/false
                    label="Activo"
                    labelPlacement='right'
                    switchKnobClassName='bg-red-300'
                    className='mt-6'
                />

            </div>

            <div className='flex justify-center p-7'>
                <Button
                    disabled={false}
                    onClick={() => formik.handleSubmit()}
                    color='primary'
                >
                    {isEdit ? 'Editar' : 'Crear'}
                </Button>
            </div>
        </Modal>
    )
}


export default ParkFormModal;
